import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './PlayingPage.css';

const PlayingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Get audio data from navigation state
  const audioData = location.state || {};
  const { audioSrc, title = 'PodTalk Audio Summary', description = 'Your personalized audio content' } = audioData;

  useEffect(() => {
    if (!audioSrc) {
      navigate('/product');
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioSrc, navigate]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    audioRef.current.playbackRate = speed;
  };

  const skipTime = (seconds) => {
    const audio = audioRef.current;
    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  if (!audioSrc) {
    return null;
  }

  return (
    <div className="playing-page">
      {/* Header */}
      <header className="playing-header">
        <div className="header-content">
          <Link to="/" className="logo">
            <h2>PodTalk</h2>
          </Link>
          <nav className="header-nav">
            <Link to="/product" className="nav-link">← Back to Feed</Link>
          </nav>
        </div>
      </header>

      {/* Main Player */}
      <main className="player-main">
        <div className="player-container">
          {/* Audio Visualization */}
          <div className="audio-visual">
            <div className="waveform">
              <div className="wave-bars">
                {[...Array(50)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ 
                      animationDelay: `${i * 0.1}s`,
                      height: `${Math.random() * 60 + 20}%`
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Track Info */}
          <div className="track-info">
            <h1>{title}</h1>
            <p>{description}</p>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="time-display">
              <span className="current-time">{formatTime(currentTime)}</span>
              <span className="duration">{formatTime(duration)}</span>
            </div>
            <div className="progress-bar" onClick={handleSeek}>
              <div className="progress-track">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progressPercent}%` }}
                ></div>
                <div 
                  className="progress-thumb" 
                  style={{ left: `${progressPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="player-controls">
            <button 
              className="control-btn skip-btn" 
              onClick={() => skipTime(-15)}
              title="Rewind 15 seconds"
            >
              ⏪
            </button>
            
            <button 
              className="control-btn play-btn" 
              onClick={togglePlayPause}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </button>
            
            <button 
              className="control-btn skip-btn" 
              onClick={() => skipTime(15)}
              title="Forward 15 seconds"
            >
              ⏩
            </button>
          </div>

          {/* Additional Controls */}
          <div className="additional-controls">
            {/* Speed Control */}
            <div className="speed-control">
              <label>Speed:</label>
              <div className="speed-buttons">
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                  <button
                    key={speed}
                    className={`speed-btn ${playbackRate === speed ? 'active' : ''}`}
                    onClick={() => handleSpeedChange(speed)}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>

            {/* Volume Control */}
            <div className="volume-control">
              <label>🔊</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="volume-slider"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="action-btn secondary">
              📥 Download
            </button>
            <button className="action-btn secondary">
              📤 Share
            </button>
            <Link to="/product" className="action-btn primary">
              🔍 Explore More
            </Link>
          </div>
        </div>
      </main>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
      />
    </div>
  );
};

export default PlayingPage; 